import { useId } from 'react';
import type { ComponentPropsWithoutRef } from 'react';
import FormField from './FormField';
import styles from './Input.module.scss';
import type { InputSize } from './TextInput';

// ============================================
// TYPES
// ============================================

export interface TextareaProps extends ComponentPropsWithoutRef<'textarea'> {
  /** Forwarded ref to the underlying <textarea> element */
  ref?: React.Ref<HTMLTextAreaElement>;
  /** Input size variant (affects font size and border radius, not height) */
  size?: InputSize;
  /** Label text displayed above the textarea */
  label?: string;
  /** Show required asterisk on the label */
  isRequired?: boolean;
  /** Help text below the textarea (hidden when error is present) */
  helperText?: string;
  /** Error message — replaces helperText, applies error border */
  error?: string;
  /** Stretch to fill parent width */
  fullWidth?: boolean;
}

// ============================================
// COMPONENT
// ============================================

function Textarea({
  ref,
  size = 'md',
  label,
  isRequired = false,
  helperText,
  error,
  fullWidth = true,
  className,
  id: idProp,
  disabled,
  ...textareaProps
}: TextareaProps) {
  const autoId = useId();
  const id = idProp ?? autoId;
  const hasError = Boolean(error);

  const describedBy = hasError
    ? `${id}-error`
    : helperText
      ? `${id}-helper`
      : undefined;

  const sizeClass = styles[size] ?? styles.md;

  const fieldClassName = [
    sizeClass,
    fullWidth && styles.fullWidth,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <FormField
      id={id}
      label={label}
      isRequired={isRequired}
      helperText={helperText}
      error={error}
      isDisabled={disabled}
      className={fieldClassName}
    >
      <textarea
        ref={ref}
        id={id}
        disabled={disabled}
        className={styles.textarea}
        aria-invalid={hasError || undefined}
        aria-required={isRequired || undefined}
        aria-describedby={describedBy}
        {...textareaProps}
      />
    </FormField>
  );
}

export default Textarea;
